import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import GoodLeapApi from "../../api/goodleap-api";

const STAGES = {
  IDLE: "idle",
  WAITING_FOR_APPROVAL: "waiting_for_approval",
  COMPLETED: "completed",
  SUBMIT_CHANGE_ORDER: "submit_change_order",
  ERROR: "error",
};

export default function GoodLeapTPOModal({
  onHide,
  show,
  isChangeOrder,
  loanA,
  sfProjectId,
  changeOrder,
}) {
  const [stage, setStage] = useState(
    isChangeOrder ? STAGES.SUBMIT_CHANGE_ORDER : STAGES.IDLE
  );
  const [stageErrors, setStageErrors] = useState([]);

  const stageComponents = [
    {
      stage: STAGES.IDLE,
      component: IdleStage,
    },
    {
      stage: STAGES.WAITING_FOR_APPROVAL,
      component: WaitingForApprovalStage,
    },
    {
      stage: STAGES.COMPLETED,
      component: CompletedStage,
    },
    {
      stage: STAGES.SUBMIT_CHANGE_ORDER,
      component: SubmitChangeOrderStage,
    },
  ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {stage === STAGES.SUBMIT_CHANGE_ORDER
            ? "Syncing and Submitting Change Order"
            : "Syncing Application"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stageComponents.map(
          (item) =>
            item.stage === stage && (
              <item.component
                key={item.stage}
                stageErrors={stageErrors}
                setStageErrors={setStageErrors}
                setStage={setStage}
                loanA={loanA}
                sfProjectId={sfProjectId}
                changeOrder={changeOrder}
              />
            )
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide()}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * 1. Idle Stage
 * - This stage will be shown when the application is first opened and before the user is redirected to the Goodleap application.
 */
function IdleStage({ stageErrors, setStageErrors, setStage, changeOrder }) {
  const errors = stageErrors.filter((item) => item.stage === STAGES.IDLE);

  // submit the loan to Goodleap and get back the link to the application
  useEffect(() => {
    changeOrder.shareLoan();
  }, []);

  // if the response has a link, set the stage to WAITING_FOR_APPROVAL, LoanApplyModal will handle the redirect
  useEffect(() => {
    if (!!changeOrder.response) {
      if (changeOrder.response.link) {
        setStage(STAGES.WAITING_FOR_APPROVAL);
      }
    }
  }, [changeOrder.response]);

  return (
    <div>
      <Spinner size="sm" style={{ marginRight: "10px" }} />
      Submitting information to Goodleap - Application will open in a new tab
    </div>
  );
}

/**
 * 2. Waiting for approval from Goodleap Stage
 * - This stage will at 5 second intervals check the application status. This will be present when when user is filling out the application.
 * - If the application is approved, the stage will be set to COMPLETED
 * - If the application is not approved, the stage will be set to WAITING_FOR_APPROVAL
 */
function WaitingForApprovalStage({
  stageErrors,
  setStageErrors,
  setStage,
  loanA,
  sfProjectId,
  changeOrder,
}) {
  const errors = stageErrors.filter(
    (item) => item.stage === STAGES.WAITING_FOR_APPROVAL
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const checkApplicationStatus = async () => {
        const res = await GoodLeapApi.checkApplicationStatus({
          sf_project_id: sfProjectId,
        });
        if (res.success === true) {
          let errors = [];
          if (res.utility.success === false) {
            errors.push({
              stage: STAGES.COMPLETED,
              id: "utility",
              error: res.utility.message,
            });
          }
          if (res.design.success === false) {
            errors.push({
              stage: STAGES.COMPLETED,
              id: "design",
              error: res.design.message,
            });
          }
          if (res.docs.success === false) {
            errors.push({
              stage: STAGES.COMPLETED,
              id: "docs",
              error: res.docs.message,
            });
          }
          setStageErrors(errors);
          setStage(STAGES.COMPLETED);
          clearInterval(interval);
        }
      };

      checkApplicationStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

    console.dir(changeOrder);
  return (
    <div>
      <Spinner size="sm" style={{ marginRight: "10px" }} />
      Waiting on approval from Goodleap... Please do not close this window. 
          {" "}
          {changeOrder?.response?.link && (
            <>
              If the application didn’t open, click this link: {" "}
              <a
                href={changeOrder.response.link}
                target="_blank"
              >
                Open Application
              </a>
            </>
          )}
    </div>
  );
}

/**
 * 3. Completed Stage
 * - This stage will be shown when the application is approved by Goodleap. It will also display any errors that may have occurred.
 * = It will direct the user to the Submit Change Order stage.
 */
function CompletedStage({
  stage,
  stageErrors,
  setStageErrors,
  setStage,
  changeOrder,
}) {
  const errors = stageErrors.filter((item) => item.stage === STAGES.COMPLETED);

  useEffect(() => {
    changeOrder.setApplied(true);
  }, []);

  useEffect(() => {
    console.log(changeOrder.response);
  }, [changeOrder.response]);

  // get a mapped list of errors for a given id
  const getErrors = (id) => {
    const _errors = errors.filter((item) => item.id === id);
    return _errors.map((item) => (
      <p
        className="modal-stage-list-item-content-error"
        key={item.id + item.error}
      >
        {item.error}
      </p>
    ));
  };

  return (
    <ul className="modal-stage-list">
      {/* Utility Information */}
      <li className="modal-stage-list-item">
        <div className="modal-stage-list-item-content-icon">
          {getErrors("utility").length === 0 ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </div>
        <div className="modal-stage-list-item-content">
          <p>Utility Information Submitted</p>
          {getErrors("utility")}
        </div>
      </li>
      {/* System design */}
      <li className="modal-stage-list-item">
        <div className="modal-stage-list-item-content-icon">
          {getErrors("design").length === 0 ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </div>
        <div className="modal-stage-list-item-content">
          <p>System Design Submitted</p>
          {getErrors("design")}
        </div>
      </li>
      {/* Docs sent */}
      <li className="modal-stage-list-item">
        <div className="modal-stage-list-item-content-icon">
          {getErrors("docs").length === 0 ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </div>
        <div className="modal-stage-list-item-content">
          <p>Docs Sent</p>
          {getErrors("docs")}
        </div>
      </li>
      {errors.length === 0 && (
        <li>
          All information was successfully submitted to Goodleap and financial
          docs have been sent to the customer.
        </li>
      )}
      {errors.length > 0 && (
        <li>
          Correct the errors above and then press the 'Submit Change Order'
          button on the financing menu to try again.
        </li>
      )}
    </ul>
  );
}

/**
 * 4. Submit Change Order Stage
 * - This stage will be shown when the user has corrected the errors and is ready to submit the change order.
 * - It will submit the change order to Goodleap and then direct the user to the final stage.
 */
function SubmitChangeOrderStage({
  stage,
  stageErrors,
  setStageErrors,
  setStage,
  changeOrder,
}) {
  const [currentStatusIndex, setCurrentStatusIndex] = useState(0);
  const errors = stageErrors.filter(
    (item) => item.stage === STAGES.SUBMIT_CHANGE_ORDER
  );

  // Status messages to cycle through
  const statusMessages = [
    "Submitting Change Order…",
    "Uploading Utility Information…",
    "Refreshing Savings Schedule…",
    "Uploading System Design Information…",
    "Validating System Design…",
    "Syncing Data…",
    "Finalizing Change Order…",
  ];

  // cycle through status messages (ui only)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStatusIndex((prevIndex) =>
        prevIndex === statusMessages.length - 1 ? prevIndex : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [statusMessages.length]);

  // submit change order
  useEffect(() => {
    changeOrder.shareLoan();
  }, []);

  // handle change order response and set stage errors if errors exist
  useEffect(() => {
    if (!changeOrder.success) {
      setStageErrors([
        {
          stage: STAGES.SUBMIT_CHANGE_ORDER,
          id: "change_order",
          error: changeOrder.changeOrderMessage,
        },
      ]);
    }
  }, [changeOrder.success, changeOrder.changeOrderMessage]);

  // ready state
  if (!changeOrder.isLoading) {
    return (
      <ul className="modal-stage-list">
        <li className="modal-stage-list-item">
          <div className="modal-stage-list-item-content-icon">
            {changeOrder.success ? (
              <CheckCircleIcon style={{ color: "green" }} />
            ) : (
              <CancelIcon style={{ color: "red" }} />
            )}
          </div>
          <div className="modal-stage-list-item-content">
            {!changeOrder.success && <p>Change Order Submission Failed</p>}
            {changeOrder.success && <p>{changeOrder.changeOrderMessage}</p>}
            {!changeOrder.success &&
              errors.map((item) => (
                <p
                  className="modal-stage-list-item-content-error"
                  key={item.id + item.error}
                >
                  {item.error}
                </p>
              ))}
          </div>
        </li>
        {!changeOrder.success && (
          <li>
            Correct the errors above and then press the 'Submit Change Order'
            button on the financing menu to try again.
          </li>
        )}
      </ul>
    );
  }

  // loading state
  if (changeOrder.isLoading) {
    return (
      <div>
        <Spinner size="sm" style={{ marginRight: "10px" }} />
        {statusMessages[currentStatusIndex]}
      </div>
    );
  }
}
